import axios from 'axios'
import TokenService from '@/services/storage.service'
import { TENANCY_SETTINGS } from '@/config'
import { isJwtExpired } from '@/auth/token'
import { resetAuthSessionExpired } from '@/auth/session'

const refreshClient = axios.create()

let refreshInFlight = null

function getAdminApiPrefix() {
  const lang = TokenService.getLang() || 'en'
  return TENANCY_SETTINGS.TENANCY ? '/api/v1/admin/' : `/api/v1/${lang}/`
}

function buildRefreshUrl() {
  const baseURL = (axios.defaults.baseURL || '').replace(/\/$/, '')
  return `${baseURL}${getAdminApiPrefix()}auth/refresh`
}

function buildRefreshHeaders(token) {
  const headers = {
    ...(axios.defaults.headers.common || {})
  }

  delete headers.common
  if (token) {
    headers.Authorization = `Bearer ${token}`
  } else {
    delete headers.Authorization
  }

  return headers
}

export function parseRefreshTokenResponse(response) {
  const data = response?.data?.data ?? response?.data ?? {}

  return {
    access: data.token || data.accessToken || null,
    refresh: data.refreshToken || null
  }
}

function applyRefreshedTokens(access, refresh) {
  TokenService.saveToken(access)
  if (refresh) {
    TokenService.saveRefreshToken(refresh)
  }
  axios.defaults.headers.common.Authorization = `Bearer ${access}`
  resetAuthSessionExpired()
}

export async function refreshAccessToken() {
  const currentToken = TokenService.getToken()
  const storedRefreshToken = TokenService.getRefreshToken()

  if (!currentToken && !storedRefreshToken) {
    throw new Error('no_token')
  }

  if (!axios.defaults.baseURL) {
    throw new Error('api_not_initialized')
  }

  if (refreshInFlight) {
    return refreshInFlight
  }

  refreshInFlight = (async () => {
    let response

    if (storedRefreshToken) {
      response = await refreshClient.post(
        buildRefreshUrl(),
        { refreshToken: storedRefreshToken },
        { headers: buildRefreshHeaders(null) }
      )
    } else {
      response = await refreshClient.post(buildRefreshUrl(), null, {
        headers: buildRefreshHeaders(currentToken)
      })
    }

    const { access, refresh } = parseRefreshTokenResponse(response)
    if (!access) {
      throw new Error('invalid_refresh_response')
    }

    applyRefreshedTokens(access, refresh)

    return access
  })()

  try {
    return await refreshInFlight
  } finally {
    refreshInFlight = null
  }
}

export async function ensureValidAccessToken() {
  const token = TokenService.getToken()
  if (!token || !isJwtExpired(token)) {
    return token
  }

  return refreshAccessToken()
}

export function syncAuthHeaderFromStorage(event) {
  if (event.key === 'token') {
    if (event.newValue) {
      axios.defaults.headers.common.Authorization = `Bearer ${event.newValue}`
      resetAuthSessionExpired()
    } else {
      delete axios.defaults.headers.common.Authorization
    }
  }
}
